import { getLanguage } from '@citrite/translate';
import {
	PermissionByType,
	PermissionsConfigurationsByType,
	PresetsByResourceType,
	PresetsByType,
} from '@sharefiledev/sharing-pilet-sdk';
import {
	EvaluatePost200Response,
	LinkSettings,
} from '@sharefiledev/sharing-pilet-sdk/api';
import { useAsync } from 'react-async-hook';
import { PermissionsHttp } from '../../../PermissionsHttp';
import { mapLanguageToEnum } from '../../../utils/share-utils';
import { WatermarkConstants } from '../../constants';
import { getRidsWithoutFileVersion } from '../../helpers/GetBaseRids';
import { WatermarkConfig } from '../../ResourceContext';
import { getPresetByTypeFromEvaluateResponse } from '../utils';

export interface GetPermissionConfigurationProps {
	accountLevelLinkSettings?: LinkSettings;
	watermarkedResponse?: WatermarkConfig;
	isWatermarkingAvailable: boolean;
}

export interface PermissionConfiguration {
	resourceType: string;
	configuration?: EvaluatePost200Response;
}

function transformPermissionConfigurationByType(
	permissionConfigurations: PermissionConfiguration[]
): PermissionsConfigurationsByType {
	const availablePermissionSetsByType: PermissionByType = {};
	const presetsByType: PresetsByResourceType = {};

	permissionConfigurations.forEach(({ resourceType, configuration }) => {
		if (!configuration) {
			return;
		}
		const newType =
			resourceType === WatermarkConstants.FileResourceType
				? WatermarkConstants.WatermarkedFileResourceType
				: WatermarkConstants.WatermarkedFileVersionResourceType;
		availablePermissionSetsByType[newType] = configuration.sharablePermissions.map(
			permissionSet => ({
				key: permissionSet.key,
				description: permissionSet.description,
				sourceLabel: permissionSet.sourceLabel,
				permissions: permissionSet.permissions,
				requireOneOfKeys: permissionSet.requireOneOfKeys,
				requireAllKeys: permissionSet.requireAllKeys,
				requiredRoles: permissionSet.requiredRoles,
			})
		);

		if (configuration.sharablePresets?.length) {
			presetsByType[newType] = configuration.sharablePresets
				.filter(preset => preset.actions?.canShare)
				.map(preset => ({
					rid: preset.rid,
					name: preset.name,
					permissionsByType: {
						[newType]: preset.permissions ?? [],
					},
					requiredRoles: preset.requiredRoles,
					presetDescription: preset.presetDescription,
					category: preset.category,
				}));
		}
	});

	return {
		availablePermissionSetsByType,
		presetsByType,
	};
}

export const usePermissionConfigurationForWatermarking = (
	props: GetPermissionConfigurationProps
): {
	configurationsByTypeForWatermarking: PermissionsConfigurationsByType;
	sharablePresetsByTypeForWatermarking: PresetsByType[];
} => {
	const watermarkedRids = props.watermarkedResponse?.transformedRids;

	const { result } = useAsync(async () => {
		if (!props.isWatermarkingAvailable) {
			return {
				configurationsByTypeForWatermarking: {
					availablePermissionSetsByType: {},
					presetsByType: {},
				},
				sharablePresetsByTypeForWatermarking: [],
			};
		}
		const configurations: PermissionConfiguration[] = [];
		const resourceTypes = [
			WatermarkConstants.FileResourceType,
			WatermarkConstants.FileVersionResourceType,
		];
		for (const resourceType of resourceTypes) {
			const rawRids =
				resourceType === WatermarkConstants.FileResourceType
					? getRidsWithoutFileVersion(watermarkedRids ?? [])
					: watermarkedRids;
			try {
				const response = await PermissionsHttp.getPermissionConfiguration({
					rids: rawRids!,
					pluginName: props.watermarkedResponse?.pluginName ?? '',
					locale: mapLanguageToEnum(getLanguage()),
				});

				configurations.push({ resourceType, configuration: response });
			} catch (error) {
				console.error(
					`Failed to fetch permission configuration for ${resourceType}:`,
					error
				);
			}
		}

		const transformed = transformPermissionConfigurationByType(configurations);

		const sharablePresets = configurations.flatMap(
			config => config.configuration?.sharablePresets ?? []
		);

		return {
			configurationsByTypeForWatermarking: transformed,
			sharablePresetsByTypeForWatermarking: getPresetByTypeFromEvaluateResponse(
				sharablePresets,
				transformed.presetsByType
			),
		};
	}, [props.isWatermarkingAvailable]);

	return (
		result ?? {
			configurationsByTypeForWatermarking: {
				availablePermissionSetsByType: {},
				presetsByType: {},
			},
			sharablePresetsByTypeForWatermarking: [],
		}
	);
};
